import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { onboardingAPI, handleApiError } from '../services/api';
import toast from 'react-hot-toast';

const GENERATION_OPTIONS = [
  { value: '1', label: 'Generation 1 — Wealth Creator / Founder', description: 'The original wealth creator or senior generation' },
  { value: '2', label: 'Generation 2 — Rising Generation', description: 'Children of the wealth creator' },
  { value: '3', label: 'Generation 3 — Grandchildren', description: 'Grandchildren of the wealth creator' },
];

const ROLE_OPTIONS = [
  { value: 'sibling', label: 'Sibling', description: 'Brother or sister' },
  { value: 'parent', label: 'Parent', description: 'Mother or father' },
  { value: 'child', label: 'Child', description: 'Son or daughter' },
  { value: 'married_in', label: 'Spouse / Married In', description: 'Married into the family' },
  { value: 'life_partner', label: 'Life Partner', description: 'Life partner of a family member' },
  { value: 'grandparent', label: 'Grandparent', description: 'Grandmother or grandfather' },
  { value: 'grandchild', label: 'Grandchild', description: 'Grandson or granddaughter' },
  { value: 'other', label: 'Other', description: 'Other family relationship' },
];

const EMPTY_MEMBER = { firstName: '', lastName: '', email: '', phone: '', role: '', partnerOf: '' };

const OnboardingPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Page state
  const [pageState, setPageState] = useState('loading'); // loading, form, submitting, success, error, expired
  const [errorMessage, setErrorMessage] = useState('');

  // Token validation data
  const [clientData, setClientData] = useState(null);

  // Form data
  const [familyName, setFamilyName] = useState('');
  const [startingGeneration, setStartingGeneration] = useState('');
  const [primaryMember, setPrimaryMember] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [members, setMembers] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  // Existing members (for reopened onboarding)
  const [existingMembers, setExistingMembers] = useState([]);
  const [editingExistingIndex, setEditingExistingIndex] = useState(null);
  const [editedExistingMembers, setEditedExistingMembers] = useState({});
  const [deletedMemberIds, setDeletedMemberIds] = useState([]);

  // Success data
  const [submittedData, setSubmittedData] = useState(null);
  const [bookingComplete, setBookingComplete] = useState(false);

  useEffect(() => {
    if (!token) {
      setPageState('error');
      setErrorMessage('No onboarding token provided. Please use the link from your email.');
      return;
    }
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await onboardingAPI.validateToken(token);
      setClientData(response.data);
      setFamilyName(response.data.familyName || '');
      if (response.data.existingMembers && response.data.existingMembers.length > 0) {
        setExistingMembers(response.data.existingMembers);
        // Pre-populate primary member from existing data
        const primary = response.data.existingMembers.find(m => m.isPrimaryContact);
        if (primary) {
          setPrimaryMember({
            firstName: primary.firstName || '',
            lastName: primary.lastName || '',
            email: primary.email || '',
            phone: primary.phone || '',
            id: primary.id
          });
        }
      }
      setPageState('form');
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.error?.message || 'Failed to validate onboarding link';
      setErrorMessage(message);
      if (status === 410) {
        setPageState('expired');
      } else {
        setPageState('error');
      }
    }
  };

  // Primary member management
  const updatePrimaryMember = (field, value) => {
    setPrimaryMember(prev => ({ ...prev, [field]: value }));
    const newErrors = { ...formErrors };
    if (newErrors.primary) {
      delete newErrors.primary[field];
    }
    setFormErrors(newErrors);
  };

  // Existing member editing
  const startEditingExisting = (index) => {
    const m = existingMembers[index];
    if (!editedExistingMembers[index]) {
      setEditedExistingMembers(prev => ({
        ...prev,
        [index]: { firstName: m.firstName, lastName: m.lastName, email: m.email, role: m.role || '', id: m.id }
      }));
    }
    setEditingExistingIndex(index);
  };

  const updateExistingMember = (index, field, value) => {
    setEditedExistingMembers(prev => ({
      ...prev,
      [index]: { ...prev[index], [field]: value }
    }));
  };

  const cancelEditingExisting = (index) => {
    setEditingExistingIndex(null);
    const updated = { ...editedExistingMembers };
    delete updated[index];
    setEditedExistingMembers(updated);
  };

  const deleteExistingMember = (index) => {
    const m = existingMembers[index];
    if (window.confirm(`Remove ${m.firstName} ${m.lastName} from the family?`)) {
      setDeletedMemberIds(prev => [...prev, m.id]);
      if (editingExistingIndex === index) setEditingExistingIndex(null);
      const updated = { ...editedExistingMembers };
      delete updated[index];
      setEditedExistingMembers(updated);
    }
  };

  // Get partner options for existing member editing
  const getExistingPartnerOptions = (currentIndex) => {
    const options = [];

    // Always include the primary contact (You) as a partner option
    if (primaryMember.firstName || primaryMember.lastName) {
      const primaryValue = primaryMember.id || 'primary_contact';
      options.push({ value: primaryValue, label: `${primaryMember.firstName} ${primaryMember.lastName}`.trim() });
    }

    // Include non-deleted, non-spouse existing members (excluding the one being edited)
    existingMembers
      .map((m, i) => ({ m, i }))
      .filter(({ m, i }) => i !== currentIndex && !deletedMemberIds.includes(m.id))
      .filter(({ m }) => m.id !== primaryMember.id) // primary already added above
      .filter(({ m, i }) => {
        const editedRole = editedExistingMembers[i]?.role || '';
        const currentRole = editedRole || m.role || '';
        const spouseRoles = ['married_in', 'life_partner', 'Married In', 'Life Partner'];
        return !spouseRoles.includes(currentRole);
      })
      .forEach(({ m }) => options.push({ value: m.id, label: `${m.firstName} ${m.lastName}` }));

    return options;
  };

  // Also include the primary contact (form-filler) as a partner option when adding new members
  const getPartnerOptionsWithExisting = (memberIndex) => {
    const options = [];
    // Always include the primary contact (You)
    if (primaryMember.firstName || primaryMember.lastName) {
      const primaryValue = primaryMember.id ? `existing_${primaryMember.id}` : 'existing_primary_contact';
      options.push({ value: primaryValue, label: `${primaryMember.firstName} ${primaryMember.lastName}`.trim() });
    }
    // Include all existing non-spouse, non-deleted members (excluding primary, already added)
    existingMembers.forEach((m, i) => {
      if (deletedMemberIds.includes(m.id)) return;
      if (m.id === primaryMember.id) return;
      const editedRole = editedExistingMembers[i]?.role || '';
      const currentRole = editedRole || m.role || '';
      const spouseRoles = ['married_in', 'life_partner', 'Married In', 'Life Partner'];
      if (!spouseRoles.includes(currentRole)) {
        options.push({ value: `existing_${m.id}`, label: `${m.firstName} ${m.lastName}` });
      }
    });
    // Include other new members (excluding current and other spouses)
    members.forEach((m, i) => {
      if (i === memberIndex) return;
      if (m.role === 'married_in' || m.role === 'life_partner') return;
      if (m.firstName || m.lastName) {
        options.push({ value: String(i), label: `${m.firstName} ${m.lastName}`.trim() });
      }
    });
    return options;
  };

  // Additional member management
  const addMember = () => {
    setMembers([...members, { ...EMPTY_MEMBER }]);
  };

  const removeMember = (index) => {
    const updated = members.filter((_, i) => i !== index);
    setMembers(updated);
    const newErrors = { ...formErrors };
    delete newErrors[`member_${index}`];
    setFormErrors(newErrors);
  };

  const updateMember = (index, field, value) => {
    const updated = [...members];
    updated[index] = { ...updated[index], [field]: value };
    // Clear partnerOf if role changes away from spouse/life_partner
    if (field === 'role' && value !== 'married_in' && value !== 'life_partner') {
      updated[index].partnerOf = '';
    }
    setMembers(updated);
    const newErrors = { ...formErrors };
    if (newErrors[`member_${index}`]) {
      delete newErrors[`member_${index}`][field];
    }
    setFormErrors(newErrors);
  };

  // Build list of members available as "partner of" targets for a given member index
  const getPartnerOptions = (memberIndex) => {
    const options = [];
    // Primary member is always an option
    if (primaryMember.firstName || primaryMember.lastName) {
      options.push({ value: 'primary', label: `${primaryMember.firstName} ${primaryMember.lastName}`.trim() || 'You (Primary)' });
    }
    // Other members (excluding current member and other spouses)
    members.forEach((m, i) => {
      if (i === memberIndex) return;
      if (m.role === 'married_in' || m.role === 'life_partner') return;
      if (m.firstName || m.lastName) {
        options.push({ value: String(i), label: `${m.firstName} ${m.lastName}`.trim() });
      }
    });
    return options;
  };

  // Validation
  const validateForm = () => {
    const errors = {};

    if (!familyName.trim()) {
      errors.familyName = 'Family name is required';
    }

    if (!startingGeneration) {
      errors.startingGeneration = 'Please select your generation';
    }

    // Validate primary member (always)
    const isReopened = existingMembers.length > 0;
    const primaryErrors = {};
    if (!primaryMember.firstName.trim()) primaryErrors.firstName = 'First name is required';
    if (!primaryMember.lastName.trim()) primaryErrors.lastName = 'Last name is required';
    if (!primaryMember.email.trim()) {
      primaryErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(primaryMember.email)) {
      primaryErrors.email = 'Invalid email address';
    }
    if (Object.keys(primaryErrors).length > 0) {
      errors.primary = primaryErrors;
    }

    // Validate additional members
    members.forEach((m, i) => {
      const memberErrors = {};
      if (!m.firstName.trim()) memberErrors.firstName = 'First name is required';
      if (!m.lastName.trim()) memberErrors.lastName = 'Last name is required';
      if (!m.email.trim()) {
        memberErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(m.email)) {
        memberErrors.email = 'Invalid email address';
      }
      if (!m.role) memberErrors.role = 'Please select a relationship';

      if (Object.keys(memberErrors).length > 0) {
        errors[`member_${i}`] = memberErrors;
      }
    });

    // Check for duplicate emails across new members (and primary if fresh)
    const newEmails = [
      ...(isReopened ? [] : [primaryMember.email.trim().toLowerCase()]),
      ...members.map(m => m.email.trim().toLowerCase())
    ].filter(e => e);
    const dupes = newEmails.filter((e, i) => newEmails.indexOf(e) !== i);
    if (dupes.length > 0) {
      if (!isReopened && dupes.includes(primaryMember.email.trim().toLowerCase())) {
        if (!errors.primary) errors.primary = {};
        errors.primary.email = 'Duplicate email address';
      }
      members.forEach((m, i) => {
        if (dupes.includes(m.email.trim().toLowerCase())) {
          if (!errors[`member_${i}`]) errors[`member_${i}`] = {};
          errors[`member_${i}`].email = 'Duplicate email address';
        }
      });
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setPageState('submitting');

    try {
      const isReopened = existingMembers.length > 0;

      // Build the new members array
      const newMembers = members.map(m => ({
        firstName: m.firstName.trim(),
        lastName: m.lastName.trim(),
        email: m.email.trim().toLowerCase(),
        phone: m.phone.trim(),
        role: m.role,
        isPrimary: false,
        partnerOf: m.partnerOf || null
      }));

      // For fresh onboarding, prepend the primary contact as a new member
      // For reopened, the primary contact is sent as an edited existing member
      const allMembers = isReopened
        ? newMembers
        : [
            {
              firstName: primaryMember.firstName.trim(),
              lastName: primaryMember.lastName.trim(),
              email: primaryMember.email.trim().toLowerCase(),
              phone: primaryMember.phone.trim(),
              role: 'primary_contact',
              isPrimary: true
            },
            ...newMembers
          ];

      // Build edited existing members array (only those that were actually edited)
      const editedMembers = Object.values(editedExistingMembers).map(m => ({
        id: m.id,
        firstName: m.firstName?.trim(),
        lastName: m.lastName?.trim(),
        email: m.email?.trim().toLowerCase(),
        role: m.role,
        partnerOfId: m.partnerOfId || null
      }));

      // When reopening, include primary contact updates as an edited member
      if (isReopened && primaryMember.id) {
        editedMembers.push({
          id: primaryMember.id,
          firstName: primaryMember.firstName.trim(),
          lastName: primaryMember.lastName.trim(),
          email: primaryMember.email.trim().toLowerCase(),
          phone: primaryMember.phone.trim()
        });
      }

      const response = await onboardingAPI.submitOnboarding({
        token,
        familyName: familyName.trim(),
        startingGeneration: parseInt(startingGeneration, 10),
        members: allMembers,
        editedMembers,
        deletedMemberIds: deletedMemberIds.length > 0
          ? deletedMemberIds.filter(id => id !== primaryMember.id)
          : undefined
      });

      setSubmittedData(response.data);
      setPageState('success');
      toast.success('Family members registered successfully!');

    } catch (error) {
      setPageState('form');
      const message = error.response?.data?.error?.message || 'Submission failed';
      toast.error(message);
    }
  };

  // ============================================================
  // RENDER STATES
  // ============================================================

  // Loading
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600">Validating your onboarding link...</p>
        </div>
      </div>
    );
  }

  // Error / Expired
  if (pageState === 'error' || pageState === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-error-light rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-secondary-900 mb-2">
            {pageState === 'expired' ? 'Link Expired' : 'Invalid Link'}
          </h2>
          <p className="text-secondary-600">{errorMessage}</p>
          <p className="text-sm text-secondary-500 mt-4">
            If you need assistance, please contact your wealth coach directly.
          </p>
        </div>
      </div>
    );
  }

  // Success — show booking widget
  if (pageState === 'success') {
    const bookingUrl = submittedData?.bookingUrl || submittedData?.calendlyUrl;

    // If booking is complete, show the final "all done" screen
    if (bookingComplete) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50/30 via-white to-primary-50/30 flex items-center justify-center px-4">
          <div className="max-w-lg w-full">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-primary-600 to-primary-600 px-8 py-10 text-white text-center">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-5">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold mb-2">You're All Set!</h1>
                <p className="text-primary-100 text-sm">
                  Your family members have been registered and your introductory meeting has been booked.
                </p>
              </div>
              <div className="px-8 py-6 text-center">
                <p className="text-secondary-600 mb-2">
                  A calendar invitation has been sent to your email with all the meeting details, including the Google Meet link.
                </p>
                <p className="text-secondary-500 text-sm">
                  You can safely close this page now.
                </p>
                <div className="mt-5 pt-5 border-t border-secondary-100">
                  <p className="text-xs text-secondary-400">The Family Legacy Bridge — Family Wealth Alignment Programme</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50/30 via-white to-primary-50/30 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Compact success banner */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-primary-600 to-primary-600 px-6 py-5 text-white">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-lg font-bold">Family Members Registered</h1>
                  <p className="text-primary-100 text-sm">
                    {submittedData?.members?.length || 0} member{(submittedData?.members?.length || 0) !== 1 ? 's' : ''} added successfully
                  </p>
                </div>
              </div>
            </div>

            {/* Compact member list */}
            <div className="px-6 py-4">
              <div className="flex flex-wrap gap-2">
                {submittedData?.members?.map((m, i) => (
                  <span key={i} className="inline-flex items-center bg-primary-50 text-primary-800 text-sm px-3 py-1.5 rounded-full">
                    {m.firstName} {m.lastName}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Booking widget — shown immediately */}
          {bookingUrl && (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-info-light px-6 py-4 border-b border-info/20">
                <h2 className="text-lg font-semibold text-info">Book Your Introductory Meeting</h2>
                <p className="text-sm text-info">Select a date and time that works for you</p>
              </div>
              <div style={{ height: '550px', overflow: 'hidden' }}>
                <iframe
                  src={bookingUrl}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title="Book a meeting"
                  style={{ border: 'none' }}
                ></iframe>
              </div>
              <div className="px-6 py-3 bg-secondary-50 border-t border-secondary-100 text-center">
                <button
                  onClick={() => setBookingComplete(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium underline"
                >
                  I've completed my booking — finish
                </button>
              </div>
            </div>
          )}

          {/* No booking URL configured */}
          {!bookingUrl && (
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <h2 className="text-lg font-bold text-secondary-900 mb-2">What Happens Next?</h2>
              <p className="text-secondary-600 text-sm">
                Your wealth coach will be in touch shortly to schedule an introductory Google Meet meeting
                where you'll walk through the survey process together.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============================================================
  // MAIN FORM
  // ============================================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50/30 via-white to-primary-50/30 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-primary-600 to-primary-600 px-8 py-8 text-white">
            <h1 className="text-2xl font-bold mb-1">Family Onboarding</h1>
            <p className="text-primary-100 text-sm">The Family Legacy Bridge — Family Wealth Alignment Programme</p>
          </div>
          <div className="px-8 py-5 bg-primary-50 border-b border-primary-100">
            <p className="text-secondary-700">
              {existingMembers.length > 0
                ? `Welcome back${clientData?.contactName ? `, ${clientData.contactName}` : ''}! You can add more family members below. Your previously registered members are shown for reference.`
                : `Welcome${clientData?.contactName ? `, ${clientData.contactName}` : ''}! Please tell us about yourself and the family members who will participate in the wealth alignment survey.`
              }
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Family Name */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <label className="block text-sm font-semibold text-secondary-700 mb-2">
              Family Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              value={familyName}
              onChange={(e) => { setFamilyName(e.target.value); setFormErrors({ ...formErrors, familyName: null }); }}
              placeholder="e.g. The Smith Family"
              readOnly={existingMembers.length > 0}
              className={`w-full px-4 py-3 rounded-lg border ${formErrors.familyName ? 'border-error bg-error-light' : existingMembers.length > 0 ? 'border-secondary-200 bg-secondary-50 text-secondary-600' : 'border-secondary-300'} focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors`}
            />
            {formErrors.familyName && <p className="text-error text-sm mt-1">{formErrors.familyName}</p>}
          </div>

          {/* Your Generation */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                <svg className="w-4 h-4 text-primary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-secondary-800">About You</h3>
                <p className="text-sm text-secondary-500">Which generation are you in the family?</p>
              </div>
            </div>
            <select
              value={startingGeneration}
              onChange={(e) => { setStartingGeneration(e.target.value); setFormErrors({ ...formErrors, startingGeneration: null }); }}
              className={`w-full px-4 py-3 rounded-lg border ${formErrors.startingGeneration ? 'border-error bg-error-light' : 'border-secondary-300'} focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors`}
            >
              <option value="">Select your generation...</option>
              {GENERATION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {formErrors.startingGeneration && <p className="text-error text-sm mt-1">{formErrors.startingGeneration}</p>}
            {startingGeneration && (
              <p className="text-secondary-500 text-xs mt-2">
                {GENERATION_OPTIONS.find(o => o.value === startingGeneration)?.description}
              </p>
            )}
          </div>

          {/* You (Primary Contact) — always shown, pre-populated when reopening */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-500 text-white text-sm font-bold mr-2">
                  1
                </span>
                You
                <span className="ml-2 text-xs font-medium bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
                  Primary Contact
                </span>
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-600 mb-1">
                  First Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={primaryMember.firstName}
                  onChange={(e) => updatePrimaryMember('firstName', e.target.value)}
                  placeholder="Your first name"
                  className={`w-full px-3 py-2.5 rounded-lg border ${formErrors.primary?.firstName ? 'border-error bg-error-light' : 'border-secondary-300'} focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
                />
                {formErrors.primary?.firstName && <p className="text-error text-xs mt-1">{formErrors.primary.firstName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-600 mb-1">
                  Last Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={primaryMember.lastName}
                  onChange={(e) => updatePrimaryMember('lastName', e.target.value)}
                  placeholder="Your last name"
                  className={`w-full px-3 py-2.5 rounded-lg border ${formErrors.primary?.lastName ? 'border-error bg-error-light' : 'border-secondary-300'} focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
                />
                {formErrors.primary?.lastName && <p className="text-error text-xs mt-1">{formErrors.primary.lastName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-600 mb-1">
                  Email Address <span className="text-error">*</span>
                </label>
                <input
                  type="email"
                  value={primaryMember.email}
                  onChange={(e) => updatePrimaryMember('email', e.target.value)}
                  placeholder="email@example.com"
                  className={`w-full px-3 py-2.5 rounded-lg border ${formErrors.primary?.email ? 'border-error bg-error-light' : 'border-secondary-300'} focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
                />
                {formErrors.primary?.email && <p className="text-error text-xs mt-1">{formErrors.primary.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-600 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={primaryMember.phone}
                  onChange={(e) => updatePrimaryMember('phone', e.target.value)}
                  placeholder="+27 82 123 4567"
                  className="w-full px-3 py-2.5 rounded-lg border border-secondary-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Existing Members (editable when reopened) */}
          {existingMembers.length > 0 && (() => {
            const visibleMembers = existingMembers
              .map((m, i) => ({ m, i }))
              .filter(({ m }) => !deletedMemberIds.includes(m.id))
              .filter(({ m }) => m.id !== primaryMember.id);
            return visibleMembers.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-secondary-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Family Members ({visibleMembers.length})
                  {deletedMemberIds.length > 0 && (
                    <span className="text-xs text-error font-normal ml-2">({deletedMemberIds.length} to be removed)</span>
                  )}
                </h3>
                <div className="space-y-2">
                  {visibleMembers.map(({ m, i }) => {
                    const editedRole = editedExistingMembers[i]?.role || '';
                    const showPartnerOf = editedRole === 'married_in' || editedRole === 'life_partner';
                    const partnerOptions = getExistingPartnerOptions(i);

                    return editingExistingIndex === i ? (
                      <div key={m.id || i} className="px-4 py-4 bg-white rounded-lg border-2 border-primary-300 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-secondary-600 mb-1">First Name</label>
                            <input
                              type="text"
                              value={editedExistingMembers[i]?.firstName || ''}
                              onChange={(e) => updateExistingMember(i, 'firstName', e.target.value)}
                              className="w-full px-3 py-2 rounded-lg border border-secondary-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-secondary-600 mb-1">Last Name</label>
                            <input
                              type="text"
                              value={editedExistingMembers[i]?.lastName || ''}
                              onChange={(e) => updateExistingMember(i, 'lastName', e.target.value)}
                              className="w-full px-3 py-2 rounded-lg border border-secondary-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-secondary-600 mb-1">Email</label>
                            <input
                              type="email"
                              value={editedExistingMembers[i]?.email || ''}
                              onChange={(e) => updateExistingMember(i, 'email', e.target.value)}
                              className="w-full px-3 py-2 rounded-lg border border-secondary-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-secondary-600 mb-1">Relationship</label>
                            <select
                              value={editedRole}
                              onChange={(e) => updateExistingMember(i, 'role', e.target.value)}
                              className="w-full px-3 py-2 rounded-lg border border-secondary-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                              <option value="">Select...</option>
                              {ROLE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                          {showPartnerOf && partnerOptions.length > 0 && (
                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-secondary-600 mb-1">Partner of</label>
                              <select
                                value={editedExistingMembers[i]?.partnerOfId || ''}
                                onChange={(e) => updateExistingMember(i, 'partnerOfId', e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-secondary-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              >
                                <option value="">Select partner...</option>
                                {partnerOptions.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                        <div className="flex justify-between">
                          <button
                            type="button"
                            onClick={() => deleteExistingMember(i)}
                            className="px-3 py-1.5 text-xs text-error hover:opacity-80 border border-error/40 hover:bg-error-light rounded-lg"
                          >
                            Remove Member
                          </button>
                          <div className="flex space-x-2">
                            <button
                              type="button"
                              onClick={() => cancelEditingExisting(i)}
                              className="px-3 py-1.5 text-xs text-secondary-600 hover:text-secondary-800 border border-secondary-300 rounded-lg"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingExistingIndex(null)}
                              className="px-3 py-1.5 text-xs text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div key={m.id || i} className="flex items-center justify-between px-4 py-3 bg-primary-50 rounded-lg border border-primary-100">
                        <div>
                          <span className="font-medium text-secondary-800">
                            {editedExistingMembers[i]?.firstName || m.firstName} {editedExistingMembers[i]?.lastName || m.lastName}
                          </span>
                          <span className="text-secondary-500 text-sm ml-2">({editedExistingMembers[i]?.email || m.email})</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
                            {ROLE_OPTIONS.find(o => o.value === (editedExistingMembers[i]?.role || ''))?.label || editedExistingMembers[i]?.role || m.role}
                          </span>
                          <button
                            type="button"
                            onClick={() => startEditingExisting(i)}
                            className="text-xs text-primary-600 hover:text-primary-800 font-medium underline"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteExistingMember(i)}
                            className="text-xs text-error hover:opacity-80 font-medium underline"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-secondary-400 mt-3">Click "Edit" to update a member, or "Remove" to delete them. Add new members below.</p>
              </div>
            );
          })()}

          {/* Additional Family Members */}
          {members.length > 0 && (
            <div className="space-y-4 mb-4">
              {members.map((member, index) => {
                const memberErrors = formErrors[`member_${index}`] || {};
                const showPartnerOf = member.role === 'married_in' || member.role === 'life_partner';
                const partnerOptions = existingMembers.length > 0
                  ? getPartnerOptionsWithExisting(index)
                  : getPartnerOptions(index);

                return (
                  <div key={index} className="bg-white rounded-2xl shadow-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-secondary-800">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-100 text-primary-700 text-sm font-bold mr-2">
                          {index + 2}
                        </span>
                        Family Member
                      </h3>
                      <button
                        type="button"
                        onClick={() => removeMember(index)}
                        className="text-error hover:opacity-80 text-sm font-medium flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* First Name */}
                      <div>
                        <label className="block text-sm font-medium text-secondary-600 mb-1">
                          First Name <span className="text-error">*</span>
                        </label>
                        <input
                          type="text"
                          value={member.firstName}
                          onChange={(e) => updateMember(index, 'firstName', e.target.value)}
                          placeholder="First name"
                          className={`w-full px-3 py-2.5 rounded-lg border ${memberErrors.firstName ? 'border-error bg-error-light' : 'border-secondary-300'} focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
                        />
                        {memberErrors.firstName && <p className="text-error text-xs mt-1">{memberErrors.firstName}</p>}
                      </div>

                      {/* Last Name */}
                      <div>
                        <label className="block text-sm font-medium text-secondary-600 mb-1">
                          Last Name <span className="text-error">*</span>
                        </label>
                        <input
                          type="text"
                          value={member.lastName}
                          onChange={(e) => updateMember(index, 'lastName', e.target.value)}
                          placeholder="Last name"
                          className={`w-full px-3 py-2.5 rounded-lg border ${memberErrors.lastName ? 'border-error bg-error-light' : 'border-secondary-300'} focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
                        />
                        {memberErrors.lastName && <p className="text-error text-xs mt-1">{memberErrors.lastName}</p>}
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-secondary-600 mb-1">
                          Email Address <span className="text-error">*</span>
                        </label>
                        <input
                          type="email"
                          value={member.email}
                          onChange={(e) => updateMember(index, 'email', e.target.value)}
                          placeholder="email@example.com"
                          className={`w-full px-3 py-2.5 rounded-lg border ${memberErrors.email ? 'border-error bg-error-light' : 'border-secondary-300'} focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
                        />
                        {memberErrors.email && <p className="text-error text-xs mt-1">{memberErrors.email}</p>}
                      </div>

                      {/* Phone */}
                      <div>
                        <label className="block text-sm font-medium text-secondary-600 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={member.phone}
                          onChange={(e) => updateMember(index, 'phone', e.target.value)}
                          placeholder="+27 82 123 4567"
                          className="w-full px-3 py-2.5 rounded-lg border border-secondary-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>

                      {/* Relationship */}
                      <div className={showPartnerOf ? '' : 'md:col-span-2'}>
                        <label className="block text-sm font-medium text-secondary-600 mb-1">
                          Relationship to You <span className="text-error">*</span>
                        </label>
                        <select
                          value={member.role}
                          onChange={(e) => updateMember(index, 'role', e.target.value)}
                          className={`w-full px-3 py-2.5 rounded-lg border ${memberErrors.role ? 'border-error bg-error-light' : 'border-secondary-300'} focus:ring-2 focus:ring-primary-500 focus:border-primary-500`}
                        >
                          <option value="">Select relationship...</option>
                          {ROLE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        {memberErrors.role && <p className="text-error text-xs mt-1">{memberErrors.role}</p>}
                        {member.role && (
                          <p className="text-secondary-500 text-xs mt-1">
                            {ROLE_OPTIONS.find(o => o.value === member.role)?.description}
                          </p>
                        )}
                      </div>

                      {/* Partner Of (conditional) */}
                      {showPartnerOf && partnerOptions.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-secondary-600 mb-1">
                            Partner of
                          </label>
                          <select
                            value={member.partnerOf}
                            onChange={(e) => updateMember(index, 'partnerOf', e.target.value)}
                            className="w-full px-3 py-2.5 rounded-lg border border-secondary-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          >
                            <option value="">Select partner...</option>
                            {partnerOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          <p className="text-secondary-500 text-xs mt-1">Which family member is their partner?</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Member Button */}
          <div className="mt-4 mb-6">
            <button
              type="button"
              onClick={addMember}
              className="w-full py-3 border-2 border-dashed border-primary-300 rounded-2xl text-primary-600 font-semibold hover:border-primary-400 hover:bg-primary-50 transition-colors flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Family Member
            </button>
          </div>

          {/* Submit */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-secondary-500">
                {1 + members.length} member{(1 + members.length) !== 1 ? 's' : ''} to register
                {existingMembers.length > 0 && ` (${existingMembers.length} already registered)`}
              </p>
              <button
                type="submit"
                disabled={pageState === 'submitting'}
                className="px-8 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md flex items-center"
              >
                {pageState === 'submitting' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Submit & Continue
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OnboardingPage;
